import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/UserOrders.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaMapMarkedAlt, FaPhoneAlt, FaCheckCircle } from "react-icons/fa";

const MyDeliveries = () => {
  const navigate = useNavigate();
  const delivery = JSON.parse(sessionStorage.getItem("active-delivery"));
  const [rawOrders, setRawOrders] = useState([]);
  const [groupedOrders, setGroupedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    fetchMyOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMyOrders = async () => {
    if (!delivery?.id) {
      toast.error("Delivery user not found in session. Please login.");
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.get(
        `https://goldenhides.in/api/user/delivery/myorder?deliveryPersonId=${delivery.id}`
      );
      const data = resp.data || {};
      const orders = data.orders ?? data;
      setRawOrders(Array.isArray(orders) ? orders : []);
    } catch (err) {
      console.error("Failed to fetch delivery orders", err);
      toast.error("Failed to fetch assigned orders from server.");
      setRawOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!rawOrders || rawOrders.length === 0) {
      setGroupedOrders([]);
      return;
    }

    const map = new Map();
    rawOrders.forEach((o) => {
      const id = o.orderId ?? `order_${Math.random().toString(36).slice(2, 9)}`;
      if (!map.has(id)) {
        map.set(id, {
          orderId: id,
          orderDate: o.orderDate,
          deliveryDate: o.deliveryDate,
          deliveryStatus: o.deliveryStatus,
          userName: o.userName,
          userPhone: o.userPhone,
          deliveryAddress: o.deliveryAddress ?? {},
          items: [],
        });
      }
      map.get(id).items.push({
        productId: o.productId,
        productName: o.productName,
        productDescription: o.productDescription,
        productImage: o.productImage,
        quantity: o.quantity,
        size: o.size,
        totalPrice: o.totalPrice,
      });
    });

    setGroupedOrders(Array.from(map.values()));
  }, [rawOrders]);

  const formatDateFromEpoch = (epochTime) => {
    if (!epochTime) return "-";
    const d = new Date(Number(epochTime));
    const opts = { day: "2-digit", month: "short", year: "numeric" };
    const datePart = d.toLocaleDateString(undefined, opts);
    const timePart = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart}, ${timePart}`;
  };

  const viewDirection = (latitude, longitude) => {
    if (!latitude || !longitude) {
      toast.error("Delivery coordinates not available for this address.");
      return;
    }
    navigate(`/delivery/route/${latitude}/${longitude}`);
  };

  const markAsDelivered = async (orderId) => {
    if (!orderId) return;
    const confirm = window.confirm(`Mark order ${orderId} as Delivered?`);
    if (!confirm) return;

    setMarking(true);
    try {
      const resp = await axios.post(
        "https://goldenhides.in/api/user/delivery/order/mark-delivered",
        { orderId },
        { headers: { "Content-Type": "application/json" } }
      );
      const res = resp.data;
      if (res?.success) {
        toast.success(
          res.responseMessage ?? `Order ${orderId} marked delivered.`
        );
        if (res.orders && Array.isArray(res.orders)) {
          setRawOrders(res.orders);
        } else {
          await fetchMyOrders();
        }
      } else {
        toast.error(
          res?.responseMessage || "Failed to update delivery status."
        );
      }
    } catch (err) {
      console.error("Mark delivered error", err);
      toast.error("Server error while updating delivery status.");
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="mt-3 px-2">
      <ToastContainer />
      <div className="container-fluid">
        <div className="text-center mb-4">
          <h2 className="fw-bold text-success">My Deliveries</h2>
          <p className="text-muted">
            Orders assigned to you — grouped by order
          </p>
        </div>

        {loading ? (
          <div className="card form-card ms-2 me-2 mb-4 custom-bg">
            <div className="card-body text-center">
              Loading your assigned orders...
            </div>
          </div>
        ) : groupedOrders.length === 0 ? (
          <div className="card form-card ms-2 me-2 mb-4 custom-bg">
            <div className="card-body text-center">
              <p className="mb-0">No assigned deliveries found.</p>
              <p className="small text-muted">
                You will see orders assigned to you here.
              </p>
            </div>
          </div>
        ) : null}

        {groupedOrders.map((order) => {
          const orderTotal = order.items.reduce(
            (acc, it) => acc + Number(it.totalPrice || 0),
            0
          );
          const address = order.deliveryAddress ?? {};
          const isDelivered = order.deliveryStatus === "Delivered";

          return (
            <div
              key={order.orderId}
              className="card form-card ms-2 me-2 mb-4 custom-bg orders-group-card"
            >
              {/* Header */}
              <div className="card-header d-flex align-items-center justify-content-between bg-white">
                <div className="d-flex align-items-center gap-4">
                  <div>
                    <div className="small text-muted">Order ID</div>
                    <div style={{ fontWeight: 700 }}>#{order.orderId}</div>
                  </div>

                  <div>
                    <div className="small text-muted">Items</div>
                    <div style={{ fontWeight: 600 }}>
                      {order.items.length} item
                      {order.items.length > 1 ? "s" : ""}
                    </div>
                  </div>

                  <div>
                    <div className="small text-muted">Ordered</div>
                    <div style={{ fontWeight: 600 }}>
                      {formatDateFromEpoch(order.orderDate)}
                    </div>
                  </div>

                  <div>
                    <div className="small text-muted">Customer</div>
                    <div style={{ fontWeight: 600 }}>
                      {order.userName || "-"}
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-3">
                  <div className="text-end small">
                    <div className="small text-muted">Contact</div>
                    <div style={{ fontWeight: 600 }}>
                      {order.userPhone || "-"}
                    </div>
                  </div>

                  <div>
                    <span
                      className={`badge order-status-badge ${
                        (order.deliveryStatus || "").toLowerCase() === "pending"
                          ? "badge-pending"
                          : "badge-other"
                      }`}
                    >
                      {order.deliveryStatus || "Pending"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="card-body">
                <div className="row g-3">
                  {order.items.map((item, idx) => (
                    <div
                      key={`${order.orderId}_item_${idx}`}
                      className="col-12 col-md-4"
                    >
                      <div className="order-product-card p-3 h-100 shadow-sm rounded">
                        <div className="d-flex gap-3">
                          <div style={{ width: 90, height: 90 }}>
                            {item.productImage ? (
                              <img
                                src={`https://goldenhides.in/api/product/${item.productImage}`}
                                alt={item.productName}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: 6,
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  background: "#f1f1f1",
                                  borderRadius: 6,
                                }}
                              />
                            )}
                          </div>

                          <div className="flex-grow-1">
                            <div style={{ fontWeight: 700 }}>
                              {item.productName}
                            </div>
                            <div className="small text-muted mt-1">
                              {item.productDescription
                                ? item.productDescription.slice(0, 80) +
                                  (item.productDescription.length > 80
                                    ? "..."
                                    : "")
                                : ""}
                            </div>

                            <div className="mt-2 small text-muted">
                              Qty: {item.quantity} &nbsp;|&nbsp; Size:{" "}
                              {item.size || "-"}
                            </div>
                          </div>

                          <div style={{ textAlign: "right", minWidth: 90 }}>
                            <div
                              className="text-success"
                              style={{ fontWeight: 700 }}
                            >
                              ₹ {Number(item.totalPrice || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="card-footer bg-white d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
                <div className="small text-muted">
                  <div>Delivery Address</div>
                  <div style={{ fontWeight: 600 }}>
                    {address.addressLine ?? "-"}, {address.city ?? "-"}
                    {address.downtown ? `, ${address.downtown}` : ""}
                  </div>
                  {address.latitude && address.longitude ? (
                    <div className="small text-muted mt-1">
                      Lat: {address.latitude}, Lng: {address.longitude}
                    </div>
                  ) : null}
                </div>

                <div className="d-flex flex-column align-items-end gap-2">
                  <div
                    style={{ fontWeight: 700, color: "#0a7a3b", fontSize: 18 }}
                  >
                    Total ₹ {orderTotal.toFixed(2)}
                  </div>

                  <div className="d-flex gap-2">
                    {/* Direction button only if NOT delivered */}
                    {!isDelivered && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          viewDirection(address.latitude, address.longitude)
                        }
                        title="Open navigation / route"
                      >
                        <FaMapMarkedAlt className="me-1" /> Direction
                      </button>
                    )}

                    <a
                      href={`tel:${order.userPhone ?? ""}`}
                      className="btn btn-sm btn-outline-success"
                      title="Call customer"
                    >
                      <FaPhoneAlt className="me-1" /> Call
                    </a>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MyDeliveries;
